import React from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useAuth } from 'hooks'
import { useDeleteAccountMutation } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'

interface DeleteAccountModalProps {
  user: any
  open: boolean
  onClose: () => void
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status?: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status
  }

  return undefined
}

export const DeleteAccountModal = (props: DeleteAccountModalProps) => {
  const { user, open, onClose = () => {} } = props
  const { logout } = useAuth()
  const { open: notification } = useSnackbar()
  const { deleteAccountAsync, isDeletingAccount } = useDeleteAccountMutation()

  const handleDeleteAccount = async () => {
    if (!user) return

    try {
      await deleteAccountAsync()
      notification({
        type: 'success',
        title: 'Account deleted successfully',
        text: 'Your account has been deleted and you will be signed out.',
      })
      handleClose()
      logout()
    } catch (error) {
      const status = getErrorStatus(error)
      const message =
        status === 403
          ? 'Cannot delete account: you are the only admin for your company'
          : getErrorMessage(error)

      notification({
        type: 'error',
        title: 'Unable to delete account',
        text: message,
      })
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      title="Delete account"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isDeletingAccount}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleDeleteAccount}
            loading={isDeletingAccount}
            disabled={isDeletingAccount}
          >
            Delete
          </Button>
        </>
      }
    >
      <Stack spacing="16px" sx={{ pb: 2 }}>
        <Typography variant="body2" color="text.secondary">{`Are you sure you want to delete this account? This cannot be undone.`}</Typography>
      </Stack>
    </Modal>
  )
}
